import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '@/services/auth.service';
import { LoginCredentials, AdminUser, AuthContextType } from '@/types/auth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Validate token on mount and page refresh
  useEffect(() => {
    const validateToken = async () => {
      try {
        // Call /auth/me to validate admin_token cookie
        // Backend will verify JWT from cookie and return user if valid
        const userData = await authService.getCurrentUser();
        
        // Token is valid - set user and store in localStorage for UI convenience
        setUser(userData);
        localStorage.setItem('admin_user', JSON.stringify(userData));
      } catch (error) {
        // Token is invalid or missing - clear everything
        setUser(null);
        localStorage.removeItem('admin_user');
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await authService.login(credentials);
      
      // Backend sets admin_token cookie (httpOnly)
      // Store user in localStorage ONLY for UI convenience (not for authentication)
      setUser(response.user);
      localStorage.setItem('admin_user', JSON.stringify(response.user));
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Call backend logout endpoint to clear admin_token and admin_user cookies
      await authService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Clear localStorage (admin_user is just for UI, not auth)
      // Both admin_token and admin_user cookies are cleared by backend
      setUser(null);
      localStorage.removeItem('admin_user');
    }
  };

  const updateUser = (userData: Partial<AdminUser>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('admin_user', JSON.stringify(updatedUser));
    }
  };

  const value: AuthContextType = {
    user,
    // Authentication state is based on user existence (which comes from token validation)
    // NOT based on localStorage admin_user
    isAuthenticated: !!user,
    login,
    logout,
    updateUser,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

